/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import Image from "next/image";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { FadeIn } from "./fade-in";

export interface HeroSectionProps {
  /** Main heading text */
  title: ReactNode;
  /** Optional subtitle text */
  subtitle?: ReactNode;
  /** Description paragraph */
  description?: ReactNode;
  /** Hero icon or element to display above title */
  icon?: ReactNode;
  /** Custom background image URL */
  backgroundImage?: string;
  /** Whether to show overlay on background image */
  showOverlay?: boolean;
  /** Additional className for the section */
  className?: string;
  /** Children to render (e.g., CTA buttons) */
  children?: ReactNode;
}

export function HeroSection({
  title,
  subtitle,
  description,
  icon,
  backgroundImage,
  showOverlay = true,
  className = "",
  children,
}: HeroSectionProps) {
  return (
    <section
      className={cn(
        "relative flex min-h-[70vh] items-center justify-center overflow-hidden py-8 md:py-16 lg:py-20",
        backgroundImage
          ? "bg-white"
          : "bg-primary text-primary-foreground grain-texture",
        className,
      )}
      id="hero"
    >
      {/* Blueprint grid pattern for dark background */}
      {!backgroundImage && (
        <div
          className="blueprint-grid absolute inset-0 opacity-10"
          aria-hidden="true"
        />
      )}

      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <Image
            fill
            priority
            alt="Construction cityscape sketch hero illustration"
            className="object-cover"
            src={backgroundImage}
            sizes="100vw"
            quality={75}
            aria-hidden="true"
          />
          {showOverlay && <div className="absolute inset-0 bg-white/80" />}
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
        {icon && (
          <FadeIn>
            <div className="mb-4 flex justify-center">
              <div className="rounded-full border border-muted bg-muted/50 p-3 backdrop-blur-sm">
                {icon}
              </div>
            </div>
          </FadeIn>
        )}

        <FadeIn delay={100}>
          <h1
            className={cn(
              "mb-4 text-[clamp(48px,8vw,80px)] leading-[1.1] tracking-tight font-bold font-display",
              backgroundImage ? "text-foreground" : "text-primary-foreground",
            )}
          >
            {title}
            {subtitle && (
              <span
                className={cn(
                  "mt-2 block text-lg font-normal md:text-xl",
                  backgroundImage
                    ? "text-muted-foreground"
                    : "text-primary-foreground/80",
                )}
              >
                {subtitle}
              </span>
            )}
          </h1>
        </FadeIn>

        {description && (
          <FadeIn delay={200}>
            <p
              className={cn(
                "mx-auto mb-6 max-w-3xl text-base leading-relaxed",
                backgroundImage
                  ? "text-foreground"
                  : "text-primary-foreground/80",
              )}
            >
              {description}
            </p>
          </FadeIn>
        )}

        {children && (
          <FadeIn delay={300}>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              {children}
            </div>
          </FadeIn>
        )}
      </div>
    </section>
  );
}
